import Card from 'components/card';
import BuyerMyOfferCard from 'components/card/OfferCard';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useNavigate } from 'react-router-dom';
import { getImage } from 'variables/images';
import SuccessModal from './components/SuccessModal';
import { useDisclosure } from '@chakra-ui/hooks';
import { ToastContainer, toast } from 'react-toastify';
import ErrorModal from './components/ErrorModal';
import { MdOutlineShoppingBag, MdFormatListBulleted, MdBarChart } from 'react-icons/md';

const convertToProperCase = (kebabCase: string): string => {
    if (!kebabCase) return '';
    return kebabCase
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const sortByDateDesc = <T extends { createdAt?: string }>(items: T[]): T[] =>
    [...items].sort(
        (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
    );

const MyOffer = () => {
    const [offer, setOffer] = useState([]);
    const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<'offers' | 'purchase-orders'>('offers');
    const [offerSubTab, setOfferSubTab] = useState<'active' | 'finished'>('active');
    const [purchaseOrderSubTab, setPurchaseOrderSubTab] = useState<'active' | 'finished'>('active');
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [user, setUser] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isOpen2,
        onOpen: onOpen2,
        onClose: onClose2,
    } = useDisclosure();
    const nav = useNavigate();

    const initOffer = () => {
        const token = localStorage.getItem('token');
        axios
            .get(`${CONFIG.apiUrl}/offer/mine`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => setOffer(res.data))
            .catch((err) => console.log(err));
    };

    const initUser = () => {
        const token = localStorage.getItem('token');
        axios
            .get(`${CONFIG.apiUrl}/user/me`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                const user = res.data;
                if (user.kyc_url) {
                    setTimeout(() => { window.location.href = user.kyc_url; }, 3000);
                }
                setUser(user);
            })
            .catch(() => {});
    };

    const initPurchaseOrders = () => {
        const token = localStorage.getItem('token');
        axios
            .get(`${CONFIG.apiUrl}/purchase-order`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                setPurchaseOrders(res.data.results || []);
                setLoadingOrders(false);
            })
            .catch(() => setLoadingOrders(false));
    };

    const deletePurchaseOrder = (orderId: string) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet ordre d'achat ?")) return;
        const token = localStorage.getItem('token');
        axios
            .delete(`${CONFIG.apiUrl}/purchase-order/${orderId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                toast.success("Ordre d'achat supprimé avec succès");
                initPurchaseOrders();
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Erreur lors de la suppression');
            });
    };

    useEffect(() => {
        initOffer();
        initUser();
        initPurchaseOrders();
        const tabParam = new URLSearchParams(window.location.search).get('tab');
        if (tabParam === 'purchase-orders') setActiveTab('purchase-orders');
    }, []);

    const activeOffers = useMemo(
        () => sortByDateDesc(offer.filter((o: any) => o.status !== 'done')),
        [offer]
    );
    const finishedOffers = useMemo(
        () => sortByDateDesc(offer.filter((o: any) => o.status === 'done')),
        [offer]
    );
    const activePurchaseOrders = useMemo(
        () => sortByDateDesc(purchaseOrders.filter((o) => o.status !== 'completed')),
        [purchaseOrders]
    );
    const finishedPurchaseOrders = useMemo(
        () => sortByDateDesc(purchaseOrders.filter((o) => o.status === 'completed')),
        [purchaseOrders]
    );
    const displayedOffers = offerSubTab === 'active' ? activeOffers : finishedOffers;
    const displayedPurchaseOrders = purchaseOrderSubTab === 'active' ? activePurchaseOrders : finishedPurchaseOrders;

    useEffect(() => {
        if (!user) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get('created') === 'true' && !isOpen) onOpen();
        if (params.get('not-paid') === 'true' && !isOpen2) onOpen2();
    }, [user]);

    const Headline = () => (
        <div className="flex flex-col gap-4 lg:flex-row">
            <div className="flex-1 rounded-xl border border-leadrs-stroke bg-white p-6">
                <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-leadrs-brown-soft">
                        <MdOutlineShoppingBag className="h-5 w-5 text-leadrs-brown-focus" />
                    </div>
                    <div>
                        <h2 className="font-crimson text-2xl font-semibold leading-tight text-leadrs-text">
                            Mes commandes
                        </h2>
                        <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                            Suivez l'évolution de vos commandes, gérez leur statut et assurez-vous que tout se déroule correctement.
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-3 lg:w-72">
                <button
                    onClick={() => nav('/dashboard')}
                    className="flex items-center gap-3 rounded-xl bg-leadrs-yellow-soft p-4 text-left hover:opacity-80 transition-opacity"
                >
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-leadrs-yellow-focus">
                        <MdFormatListBulleted className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <p className="font-geist text-sm font-semibold text-leadrs-yellow-focus">Nouvelle commande</p>
                        <p className="font-geist text-xs text-leadrs-yellow-focus opacity-70">Passer une commande de leads</p>
                    </div>
                </button>
                <button
                    onClick={() => nav('/dashboard?orderType=purchase-order')}
                    className="flex items-center gap-3 rounded-xl bg-leadrs-green-soft p-4 text-left hover:opacity-80 transition-opacity"
                >
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-leadrs-green-focus">
                        <MdBarChart className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <p className="font-geist text-sm font-semibold text-leadrs-green-focus">Ordre d'achat</p>
                        <p className="font-geist text-xs text-leadrs-green-focus opacity-70">Automatiser vos achats de leads</p>
                    </div>
                </button>
            </div>
        </div>
    );

    return (
        <div>
            <ToastContainer />
            <SuccessModal isOpen={isOpen} onClose={onClose} />
            <ErrorModal isOpen={isOpen2} onClose={onClose2} />
            <div className="h-full w-full px-1">
                <Headline />

                {/* Tabs */}
                <div className="mt-5 border-b border-leadrs-stroke">
                    <div className="flex gap-1">
                        <button
                            onClick={() => setActiveTab('offers')}
                            className={`pb-3 px-4 font-geist text-sm transition-all ${
                                activeTab === 'offers'
                                    ? 'border-b-2 border-leadrs-brand font-semibold text-leadrs-brand'
                                    : 'font-medium text-leadrs-text-soft hover:text-leadrs-text'
                            }`}
                        >
                            Mes commandes
                        </button>
                        <button
                            onClick={() => setActiveTab('purchase-orders')}
                            className={`pb-3 px-4 font-geist text-sm transition-all ${
                                activeTab === 'purchase-orders'
                                    ? 'border-b-2 border-leadrs-green-focus font-semibold text-leadrs-green-focus'
                                    : 'font-medium text-leadrs-text-soft hover:text-leadrs-text'
                            }`}
                        >
                            Mes ordres d'achat
                        </button>
                    </div>
                </div>

                {/* Offers tab */}
                {activeTab === 'offers' && (
                    <div className="mt-6">
                        {offer.length === 0 ? (
                            <div className="flex flex-col items-center py-16 gap-4">
                                <p className="font-geist text-base text-leadrs-text-soft">
                                    Vous n'avez pas encore passé commande
                                </p>
                                <button
                                    className="rounded-xl bg-leadrs-brand px-6 py-3 font-geist text-sm font-medium text-white transition hover:opacity-80"
                                    onClick={() => nav('/create-order')}
                                >
                                    Passer ma première commande
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="mb-4 flex gap-1 rounded-lg bg-leadrs-bg p-1 w-fit">
                                    <button
                                        onClick={() => setOfferSubTab('active')}
                                        className={`rounded-md px-3 py-1.5 font-geist text-xs font-medium transition-all ${
                                            offerSubTab === 'active'
                                                ? 'bg-white text-leadrs-text shadow-sm'
                                                : 'text-leadrs-text-soft hover:text-leadrs-text'
                                        }`}
                                    >
                                        En cours ({activeOffers.length})
                                    </button>
                                    <button
                                        onClick={() => setOfferSubTab('finished')}
                                        className={`rounded-md px-3 py-1.5 font-geist text-xs font-medium transition-all ${
                                            offerSubTab === 'finished'
                                                ? 'bg-white text-leadrs-text shadow-sm'
                                                : 'text-leadrs-text-soft hover:text-leadrs-text'
                                        }`}
                                    >
                                        Terminées ({finishedOffers.length})
                                    </button>
                                </div>
                                {displayedOffers.length === 0 ? (
                                    <div className="flex flex-col items-center py-16 gap-2">
                                        <p className="font-geist text-base text-leadrs-text-soft">
                                            {offerSubTab === 'active'
                                                ? 'Aucune commande en cours'
                                                : 'Aucune commande terminée'}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                                        {displayedOffers.map((item: any) => (
                                            <BuyerMyOfferCard
                                                key={item._id}
                                                bidders={[]}
                                                industry={item.industry}
                                                channels={item.channels}
                                                offerNumber={item.offerBuyerNumber}
                                                subIndustry={item.subIndustry}
                                                targetPrice={item.status === 'locked' ? item.buyerPrice : item.targetPrice}
                                                weeklyQuantity={Number(item.weeklyQuantity)}
                                                leadsGenerated={Number(item.leadsGenerated)}
                                                otherIndustry={item.otherIndustry}
                                                user={user}
                                                status={item.status}
                                                image={getImage(item.industry, item.subIndustry)}
                                                createdAt={item.createdAt}
                                                onClick={() => nav(`/my-offers/${item._id}`)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Purchase orders tab */}
                {activeTab === 'purchase-orders' && (
                    <div className="mt-6">
                        {loadingOrders ? (
                            <div className="flex items-center justify-center py-16">
                                <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-leadrs-brand" />
                            </div>
                        ) : purchaseOrders.length === 0 ? (
                            <div className="flex flex-col items-center py-16 gap-4">
                                <p className="font-geist text-base text-leadrs-text-soft">
                                    Vous n'avez pas encore d'ordre d'achat automatique
                                </p>
                                <button
                                    className="rounded-xl bg-leadrs-green-focus px-6 py-3 font-geist text-sm font-medium text-white transition hover:opacity-80"
                                    onClick={() => nav('/purchase-order')}
                                >
                                    Créer mon premier ordre d'achat
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="mb-4 flex gap-1 rounded-lg bg-leadrs-bg p-1 w-fit">
                                    <button
                                        onClick={() => setPurchaseOrderSubTab('active')}
                                        className={`rounded-md px-3 py-1.5 font-geist text-xs font-medium transition-all ${
                                            purchaseOrderSubTab === 'active'
                                                ? 'bg-white text-leadrs-text shadow-sm'
                                                : 'text-leadrs-text-soft hover:text-leadrs-text'
                                        }`}
                                    >
                                        En cours ({activePurchaseOrders.length})
                                    </button>
                                    <button
                                        onClick={() => setPurchaseOrderSubTab('finished')}
                                        className={`rounded-md px-3 py-1.5 font-geist text-xs font-medium transition-all ${
                                            purchaseOrderSubTab === 'finished'
                                                ? 'bg-white text-leadrs-text shadow-sm'
                                                : 'text-leadrs-text-soft hover:text-leadrs-text'
                                        }`}
                                    >
                                        Terminés ({finishedPurchaseOrders.length})
                                    </button>
                                </div>
                                {displayedPurchaseOrders.length === 0 ? (
                                    <div className="flex flex-col items-center py-16 gap-2">
                                        <p className="font-geist text-base text-leadrs-text-soft">
                                            {purchaseOrderSubTab === 'active'
                                                ? "Aucun ordre d'achat en cours"
                                                : "Aucun ordre d'achat terminé"}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                                        {displayedPurchaseOrders.map((order) => (
                                            <div
                                                key={order._id}
                                                className="flex flex-col w-full bg-white border border-leadrs-stroke rounded-xl overflow-hidden hover:shadow-md transition-shadow duration-200"
                                            >
                                                <img
                                                    src={getImage(
                                                        convertToProperCase(order.industry),
                                                        convertToProperCase(order.product)
                                                    )}
                                                    className="w-full h-[155px] object-cover"
                                                    alt=""
                                                />
                                                <div className="p-4 flex flex-col gap-2.5">
                                                    <div>
                                                        <p className="text-xs font-geist text-leadrs-text-soft">
                                                            {convertToProperCase(order.product)}
                                                        </p>
                                                        <p className="font-crimson text-[22px] font-semibold text-leadrs-text leading-tight">
                                                            {convertToProperCase(order.industry)}
                                                        </p>
                                                    </div>

                                                    {order.createdAt && (
                                                        <p className="text-xs font-geist text-leadrs-text-soft">
                                                            Créé le {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                                                        </p>
                                                    )}

                                                    {order.departments?.length > 0 && (
                                                        <p className="text-xs font-geist text-leadrs-text-soft">
                                                            {order.departments.join(', ').length > 50
                                                                ? order.departments.join(', ').substring(0, 50) + '...'
                                                                : order.departments.join(', ')}
                                                        </p>
                                                    )}

                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div>
                                                            <p className="text-xs font-geist text-leadrs-text-soft">Prix max</p>
                                                            <p className="text-sm font-geist font-semibold text-leadrs-text">
                                                                {order?.maxPricePerLead?.toFixed(2)}€/lead
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-geist text-leadrs-text-soft">Reçus</p>
                                                            <p className="text-sm font-geist font-semibold text-leadrs-text">
                                                                {order.totalLeadsDelivered || 0}/{order.leadCount}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <div className="flex justify-between mb-1.5">
                                                            <span className="text-xs font-geist text-leadrs-text-soft">Progression</span>
                                                            <span className="text-xs font-geist font-medium text-leadrs-text">
                                                                {Math.min(Math.round(((order.leadsReceived || 0) / order.leadCount) * 100), 100)}%
                                                            </span>
                                                        </div>
                                                        <div className="w-full rounded-full bg-leadrs-stroke h-2">
                                                            <div
                                                                className="h-2 rounded-full bg-leadrs-brand transition-all"
                                                                style={{
                                                                    width: `${Math.min(
                                                                        ((order.leadsReceived || 0) / order.leadCount) * 100,
                                                                        100
                                                                    )}%`,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center justify-between">
                                                        <span
                                                            className={`w-fit rounded-full px-3 py-1 text-xs font-medium font-geist ${
                                                                order.status === 'active'
                                                                    ? 'bg-leadrs-green-soft text-leadrs-green-focus'
                                                                    : order.status === 'completed'
                                                                    ? 'bg-leadrs-purple-soft text-leadrs-purple-focus'
                                                                    : order.status === 'pending_payment'
                                                                    ? 'bg-leadrs-yellow-soft text-leadrs-yellow-focus animate-pulse'
                                                                    : 'bg-leadrs-stroke text-leadrs-text-soft'
                                                            }`}
                                                        >
                                                            {order.status === 'active' && 'Actif'}
                                                            {order.status === 'completed' && 'Terminé'}
                                                            {order.status === 'cancelled' && 'Annulé'}
                                                            {order.status === 'pending_payment' && 'En attente de paiement'}
                                                        </span>
                                                        <button
                                                            className="font-geist text-xs text-leadrs-text-soft hover:text-red-500 transition-colors"
                                                            onClick={() => deletePurchaseOrder(order._id)}
                                                        >
                                                            Supprimer
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyOffer;
